import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../../msalConfig';
import { getItems, FAMILY_LISTS } from '../../api/figApi';
import MediaCover from '../components/MediaCover';

function groupByBookTitle(items) {
  const bookMap = new Map();
  const singles = [];
  for (const item of items) {
    if (!item.BookTitle) { singles.push(item); continue; }
    if (!bookMap.has(item.BookTitle)) bookMap.set(item.BookTitle, []);
    bookMap.get(item.BookTitle).push(item);
  }
  const books = [];
  for (const [bookTitle, chapters] of bookMap) {
    const primary = [...chapters].sort((a, b) => {
      const an = a.ChapterNumber ?? Infinity;
      const bn = b.ChapterNumber ?? Infinity;
      if (an !== bn) return an - bn;
      return (b.Created ?? '').localeCompare(a.Created ?? '');
    })[0];
    const latestCreated = chapters.reduce(
      (latest, c) => ((c.Created ?? '') > (latest ?? '') ? c.Created : latest), ''
    );
    books.push({
      id:            primary.id,
      Title:         bookTitle,
      CoverImageUrl: primary.CoverImageUrl,
      BookAuthor:    primary.BookAuthor,
      SeriesName:    primary.SeriesName,
      BookNumber:    primary.BookNumber,
      Created:       latestCreated,
      _isBook:       true,
      _chapterCount: chapters.length,
      _chapters:     chapters,
    });
  }
  return [...books, ...singles];
}

export default function Audiobooks() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const [items, setItems]             = useState(null); // null = loading
  const [error, setError]             = useState(null);
  const [sortBy, setSortBy]           = useState('recent');
  const [filterValue, setFilterValue] = useState('all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const all = await getItems(token, FAMILY_LISTS.MEDIA_ITEMS);
        if (!cancelled) {
          setItems(all.filter(item => item.MediaType === 'Audiobook'));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  // Distinct non-empty SeriesName values for the filter dropdown
  const seriesOptions = items
    ? [...new Set(items.map(i => i.SeriesName).filter(Boolean))].sort()
    : [];

  // Build display list: filter then sort
  let displayed = items ? groupByBookTitle(items) : [];
  if (filterValue !== 'all') {
    displayed = displayed.filter(i => i.SeriesName === filterValue);
  }
  displayed = [...displayed].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return (b.Created ?? '').localeCompare(a.Created ?? '');
      case 'title':
        return (a.Title ?? '').toLowerCase().localeCompare((b.Title ?? '').toLowerCase());
      case 'author': {
        const aa = (a.BookAuthor ?? '').toLowerCase();
        const ba = (b.BookAuthor ?? '').toLowerCase();
        if (!aa && ba) return 1;
        if (aa && !ba) return -1;
        return aa.localeCompare(ba);
      }
      case 'series': {
        const as = (a.SeriesName ?? '').toLowerCase();
        const bs = (b.SeriesName ?? '').toLowerCase();
        if (!as && bs) return 1;
        if (as && !bs) return -1;
        if (as !== bs) return as.localeCompare(bs);
        return (a.BookNumber ?? 0) - (b.BookNumber ?? 0);
      }
      default:
        return 0;
    }
  });

  return (
    <div className="FV-shelf">
      <h1 className="FV-shelf-title">Audiobooks</h1>

      <div className="fv-browse-toolbar">
        <select
          className="fv-browse-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort by"
        >
          <option value="recent">Recently Added</option>
          <option value="title">Title</option>
          <option value="author">Author</option>
          <option value="series">Series</option>
        </select>

        <select
          className="fv-browse-select"
          value={filterValue}
          onChange={e => setFilterValue(e.target.value)}
          aria-label="Filter by series"
        >
          <option value="all">All Series</option>
          {seriesOptions.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {items === null && !error && (
        <p className="FV-empty">Loading…</p>
      )}

      {error && (
        <p className="fv-browse-error">{error}</p>
      )}

      {items !== null && !error && displayed.length === 0 && (
        <p className="fv-browse-empty">
          {filterValue !== 'all' ? 'No audiobooks match that filter.' : 'No audiobooks yet.'}
        </p>
      )}

      {displayed.length > 0 && (
        <div className="fv-browse-grid">
          {displayed.map(item => (
            <div
              key={item.id}
              className="fv-browse-card"
              onClick={() => navigate(`/audiobooks/${item.id}`)}
            >
              <MediaCover item={item} aspectRatio="1:1" size="md" />
              <span className="fv-browse-card-title">{item.Title}</span>
              {item.BookAuthor && (
                <span className="fv-browse-card-secondary">{item.BookAuthor}</span>
              )}
              {item._isBook && (
                <span className="fv-browse-card-chapter-count">{item._chapterCount} chapters</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
